/**
 * Reload / tab-restore telemetry.
 *
 * Background: the booking cart is persisted (sessionStorage + a localStorage
 * backup), but the Checkout contact form (first/last name, email, phone) lives
 * in component-local React state and is NOT persisted. So any full-page reload
 * on the checkout step silently wipes the customer's contact details and
 * spawns a fresh PaymentIntent on remount — surfacing as the
 * "the page refreshes and deletes my data" reports.
 *
 * The two known triggers are environment-specific (which is why most customers
 * are unaffected):
 *   1. Mobile Safari discarding/reloading a backgrounded tab (e.g. after the
 *      customer app-switches to fetch a card or an SMS code).
 *   2. lazyWithReload forcing a one-shot reload when a JS chunk fails to load
 *      (stale cached index.html after an atomic deploy, content blockers, etc.).
 *
 * This module makes both visible in Sentry, PII-free. The checkout case is
 * reported at error level so the on-error Session Replay records the
 * post-reload session (cart present, form empty); other steps and bfcache
 * restores are reported at warning level.
 *
 * Keep-or-revert: this instrumentation is intended to stay (low-noise, fires
 * only on an undesirable reload). The elevated Session Replay sampling and the
 * merchant `debug` flag are the temporary pieces to dial back afterwards.
 */

import { captureError, captureWarning } from './sentry';

// Known booking step path segments (mirrors BookingFlow's route table).
const BOOKING_STEPS = [
  'locations', 'timing', 'duration', 'date', 'time', 'start-end',
  'category', 'products', 'delivery-area', 'delivery-address', 'checkout',
];

// Mirrors lazyWithReload's flag so we can label a reload as chunk-triggered.
const CHUNK_RELOAD_FLAG_KEY = 'chunk-reload-attempt';
const CHUNK_RELOAD_TTL_MS = 60 * 1000;

// Resolve the current booking step from the hash route (the app uses
// HashRouter, e.g. "#/clickandpaddle/checkout" or
// "#/clickandpaddle/<location-slug>/checkout"). Returns the deepest path
// segment that is a recognised step, or null when off the booking flow.
function currentStep() {
  if (typeof window === 'undefined') return null;
  const hash = window.location.hash || '';
  const path = hash.startsWith('#/') ? hash.slice(1) : window.location.pathname;
  const segments = path.split('/').filter(Boolean);
  for (let i = segments.length - 1; i >= 0; i--) {
    if (BOOKING_STEPS.includes(segments[i])) return segments[i];
  }
  return null;
}

// True when a persisted cart exists — distinguishes "reload lost in-progress
// data" from "reload on an empty flow".
function hasPersistedCart() {
  try {
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith('bookingData-')) return true;
    }
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith('bookingDataBackup-')) return true;
    }
  } catch { /* storage unavailable (private mode / quota) */ }
  return false;
}

// Navigation Timing type for this document load: 'navigate' | 'reload' |
// 'back_forward' | 'prerender'. We only act on 'reload'.
function navigationType() {
  try {
    const nav = performance.getEntriesByType('navigation')[0];
    return nav?.type || null;
  } catch {
    return null;
  }
}

// If lazyWithReload set its flag within the TTL, this reload was chunk-driven.
function reloadReason() {
  try {
    const raw = sessionStorage.getItem(CHUNK_RELOAD_FLAG_KEY);
    if (raw) {
      const ts = parseInt(raw, 10);
      if (!Number.isNaN(ts) && Date.now() - ts < CHUNK_RELOAD_TTL_MS) {
        return 'chunk-load';
      }
    }
  } catch { /* ignore */ }
  return 'unknown'; // most likely mobile-Safari tab discard or a manual refresh
}

export function initReloadTelemetry() {
  if (typeof window === 'undefined') return;

  // (1) A *document* load that landed directly on a booking step.
  //
  // In normal use the flow reaches each step via in-app (SPA) routing, which
  // does NOT create a new document load — so a fresh document load that lands
  // on a step is the signature of a reload, an iOS Safari tab-discard, or a
  // history restore. We originally flagged only navigationType === 'reload',
  // but the confirmed iOS case (Mobile Safari discarding the heavy checkout
  // tab) loads as 'back_forward'/'navigate' and slipped through. Widen to
  // those, gating 'navigate' on a restored cart so genuinely fresh entries
  // aren't flagged. navType is recorded so true discards can be segmented from
  // (rare) bookmarked re-entries.
  try {
    const type = navigationType();
    const step = currentStep();
    if (step && type && type !== 'prerender') {
      const restored = hasPersistedCart();
      const isReloadish = type === 'reload' || type === 'back_forward';
      const isRestoredNavigate = type === 'navigate' && restored && step === 'checkout';
      if (isReloadish || isRestoredNavigate) {
        const extras = {
          step,
          navType: type,
          reloadReason: reloadReason(),
          hadPersistedCart: restored,
          // Strip query/hash params — keep the bare path, never PII.
          url: window.location.href.split('?')[0],
        };
        if (step === 'checkout') {
          // Error level → triggers on-error Session Replay of the post-reload
          // session, which shows the emptied contact form and the new PI.
          captureError(
            new Error('Page reload on checkout — contact form state lost'),
            extras,
            { flow: 'payment', stage: 'reload-on-step', reload_step: step, reload_nav_type: type },
          );
        } else {
          captureWarning(
            `Page reload on booking step: ${step}`,
            extras,
            { flow: 'reload', stage: 'reload-on-step', reload_step: step, reload_nav_type: type },
          );
        }
      }
    }
  } catch { /* never break app startup */ }

  // (2) Back/forward-cache restore (Safari/Chrome restoring a backgrounded
  // tab from bfcache). Form state may or may not survive; log it so these can
  // be correlated with "page refreshed" reports.
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    const step = currentStep();
    if (!step) return;
    captureWarning(
      'Tab restored from bfcache on booking step',
      { step, hadPersistedCart: hasPersistedCart() },
      { flow: 'reload', stage: 'tab-restore', reload_step: step },
    );
  });
}
